import styles from './DatabaseERModel.module.css';

import React, { useCallback, useMemo } from 'react';
import { addEdge, BaseEdge, EdgeLabelRenderer, getBezierPath, Handle, MarkerType, Position } from 'reactflow';

import UUID from 'bng/utils/UUID';
import Icon from 'components/ui/common/Icon';
import BngIconButton from 'components/bng/ui/BngIconButton';
import ERModel from 'components/ui/common/ERModel';
import BngButton from 'components/bng/ui/BngButton';
import BngDropdown from 'components/bng/ui/BngDropdown';
import useTranslation from 'components/hooks/useTranslation';
import BngCheckbox from 'components/bng/form/BngCheckbox';
import useReduxDispatch from 'components/hooks/useReduxDispatch';
import AssistedModeFilterDialog from 'components/bng/pages/admin/structures/bimQuery/dialogs/AssistedModeFilterDialog';
import { MODALS } from 'components/ui/redux/Actions';
import PreviewDataDialog from 'components/bng/pages/admin/structures/bimQuery/dialogs/PreviewDataDialog';
import { useFormikContext } from "formik";

const findFieldIcon = (fieldType) => {
  switch (fieldType) {
    case 'NUMERIC':
      return '123';
    case 'DATE':
      return 'schedule';
    case 'BOOL':
      return 'memory';
    default:
      return 'abc';
  }
};

function DatabaseTableNode({ id = UUID.randomUUID(), selected = false, data = {}, formikProps }) {
  const dispatch = useReduxDispatch();

  const tableInfo = data.tableInfo;
  const { values, setFieldValue } = useFormikContext();

  const handleFieldAction = useCallback(
    (selectedState, fieldName) => {
      const updatedNodes = values.bimQueryMetadata.nodes.map((node) => {
        if (node.id === id) {
          const updatedFields = node.data.tableInfo.fields.map((field) => {
            if (fieldName && field.name === fieldName) {
              return { ...field, selected: selectedState };
            }
            return fieldName ? field : { ...field, selected: selectedState };
          });

          node = _.cloneDeep(node);
          node.data.tableInfo.fields = updatedFields;
        }

        return node;
      });
      setFieldValue('bimQueryMetadata.nodes', updatedNodes);
    },
    [values, id]
  );

  return (
    <div className={`DatabaseTableNode ${styles.DatabaseTableNode}`}>
      <div className={`${styles.databaseTableNode} ${selected ? styles.selected : ''}`}>
        <div className={styles.tableHeader}>
          <div className={styles.tableName}>
            <Icon icon={'table_chart'} style={{ fontSize: '16px' }} />
            <span>{tableInfo.table}</span>
          </div>
          <div className={styles.tableActions}>
            <BngIconButton
              icon={'filter_alt'}
              className="nodrag"
              onClick={() =>
                dispatch(
                  MODALS.open(AssistedModeFilterDialog, {
                    nodeId: id,
                    nodes: values.bimQueryMetadata.nodes,
                    onChange: (nodes) => setFieldValue('bimQueryMetadata.nodes', nodes),
                  })
                )
              }
            />
            <DatabaseERModelDetails
              onSelectAll={() => handleFieldAction(true)}
              onUncheckAll={() => handleFieldAction(false)}
              onDeleteTable={() => {
                const updatedNodes = values.bimQueryMetadata.nodes.filter((node) => node.id !== id);
                const updatedTableQueries = values.bimQueryMetadata.tableQueries.filter((tq) => tq.id !== id);
                const updatedFieldConfigs = values.fieldConfigs.map((fc) =>
                  fc.nodeId === id ? { ...fc, visibleOnUi: false } : fc
                );
                const updatedEdges = values.bimQueryMetadata.edges.filter(
                  edge => edge.source !== id && edge.target !== id
                );

                setFieldValue('bimQueryMetadata.nodes', updatedNodes);
                setFieldValue('bimQueryMetadata.edges', updatedEdges);
                setFieldValue('bimQueryMetadata.tableQueries', updatedTableQueries);
                setFieldValue('fieldConfigs', updatedFieldConfigs, false);
              }}
              onPreviewData={() => {
                dispatch(
                  MODALS.open(PreviewDataDialog, {
                    connectionId: values.connection,
                    nodeId: id,
                    formikValues: values,
                  })
                );
              }}
            />
          </div>
        </div>
        <div className={styles.fieldWrapper}>
          {tableInfo.fields.map((field, idx) => {
            const handleId = `${id}-field-${field.name}`;
            return (
              <div className={styles.field} key={idx}>
                <Handle
                  type="target"
                  id={`target-left-${handleId}`}
                  position={Position.Left}
                  style={{
                    top: '50%',
                    left: '-8px',
                  }}
                  className={`${styles.tableHandle}`}
                  isConnectable
                />
                <Handle
                  type="source"
                  id={`source-left-${handleId}`}
                  position={Position.Left}
                  style={{
                    top: '50%',
                    left: '-8px',
                  }}
                  className={`${styles.tableHandle}`}
                  isConnectable
                />

                <Handle
                  type="target"
                  id={`target-right-${handleId}`}
                  position={Position.Right}
                  style={{
                    top: '50%',
                    right: '-8px',
                  }}
                  className={`${styles.tableHandle}`}
                  isConnectable
                />
                <Handle
                  type="source"
                  id={`source-right-${handleId}`}
                  position={Position.Right}
                  style={{
                    top: '50%',
                    right: '-8px',
                  }}
                  className={`${styles.tableHandle}`}
                  isConnectable
                />

                <BngCheckbox
                  className="nodrag"
                  field={{
                    onChange: () => handleFieldAction(!field.selected, field.name),
                    value: field.selected,
                  }}
                  style={{ minHeight: '15px' }}
                />
                <Icon icon={findFieldIcon(field.type)} />
                <span>{field.name}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DatabaseEdge({ id = UUID.randomUUID(), selected = false, ...props }) {
  const { t } = useTranslation();
  const [edgePath, labelX, labelY] = getBezierPath(props);

  return (
    <>
      <BaseEdge id={id} path={edgePath} {...props} />
      <EdgeLabelRenderer>
        <div
          className={`DatabaseEdge ${styles.databaseEdge}`}
          style={{
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
          }}
        >
          <BngDropdown
            popperClassName={styles.bngDropdown}
            customButton={({ openDropdown }) => (
              <BngButton
                icon={'link'}
                className={styles.edgeIconButton}
                onClick={(e) => {
                  openDropdown(e);
                }}
              />
            )}
            customOptions={({ closeDropdown }) => {
              return (
                <div className={styles.edgeDropdown}>
                  <span className={styles.dropdownTitle}>{t('interaction')}</span>
                </div>
              );
            }}
          />
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

function DatabaseERModelDetails({ onSelectAll, onUncheckAll, onDeleteTable, onPreviewData }) {
  const { t } = useTranslation();

  return (
    <BngDropdown
      className="nodrag"
      style={{ height: '250px' }}
      popperClassName={styles.bngDropdown}
      customButton={({ openDropdown }) => {
        return (
          <BngIconButton
            icon={'more_vert'}
            onClick={(e) => {
              e.stopPropagation();
              openDropdown(e);
            }}
            className={styles.headersButtons}
          />
        );
      }}
      options={[
        [
          {
            label: t('structure.preview'),
            icon: 'visibility',
            onClick: onPreviewData,
          },
          {
            label: t('remove.structure'),
            icon: 'close',
            onClick: onDeleteTable,
          },
        ],
        [
          {
            label: t('select.all.columns'),
            icon: 'check',
            onClick: onSelectAll,
          },
          {
            label: t('uncheck.all.columns'),
            icon: 'remove_done',
            onClick: onUncheckAll,
          },
        ],
      ]}
    />
  );
}

const edgeTypes = {
  databaseEdge: DatabaseEdge,
};

export default function DatabaseERModel({ initialNodes, initialEdges, onNewNode = _.noop, formikProps }) {
  const nodeTypes = useMemo(
    () => ({
      databaseTable: (props) => <DatabaseTableNode {...props} />,
    }),
    []
  );

  return (
    <ERModel
      className="DatabaseERModel"
      showControls={true}
      showMinimap={false}
      showNewNodeButton={false}
      initialNodes={initialNodes}
      initialEdges={initialEdges}
      onNewNode={onNewNode}
      onChange={({ nodes, edges }) => {
        formikProps.setFieldValue('bimQueryMetadata.nodes', nodes);
        formikProps.setFieldValue('bimQueryMetadata.edges', edges);
      }}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      onNewEdge={({ params, edges, setEdges }) => {
        const { source, target, sourceHandle, targetHandle } = params;

        if (source == target) {
          return;
        }

        const newEdge = {
          id: `${sourceHandle}->${targetHandle}`,
          source: source,
          target: target,
          type: 'databaseEdge',
          sourceHandle: sourceHandle,
          targetHandle: targetHandle,
          style: {
            strokeWidth: 2,
            stroke: '#005dff',
          },
        };
        setEdges((eds) => {
          const updated = addEdge(newEdge, eds);
          formikProps.setFieldValue('bimQueryMetadata.edges', updated);
          return updated;
        });
      }}
    />
  );
}
